import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGift, faCopy, faLock, faCalendarDays, faArrowLeft,
  faInfoCircle, faCheck, faVial, faSpinner, faXmark,
  faTriangleExclamation, faHourglass
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import BackButton from '../../components/BackButton/BackButton';
import './SponsoredPackages.css';
import { useProfile } from '../../context/ProfileContext';

/* ------------------------------------------------
   Interfaces
   ------------------------------------------------ */
interface PackageRedemptionInfo {
  id: number;
  coupon_code: string;
  redeemed_at: string;
  status: string;
}

interface SponsoredPackage {
  id: number;
  name: string;
  description: string | null;
  tests_included: string[];
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  is_expired: boolean;
  is_available: boolean;
  is_upcoming: boolean;
  instructions: string | null;
  my_redemption: PackageRedemptionInfo | null;
}

/* ------------------------------------------------
   Helpers
   ------------------------------------------------ */
const formatDate = (dateStr: string): string => {
  const dt = new Date(dateStr);
  return dt.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getCardStatusClass = (pkg: SponsoredPackage): string => {
  if (pkg.is_expired && !pkg.my_redemption) return 'sp-card--expired';
  return '';
};

/* ------------------------------------------------
   Component
   ------------------------------------------------ */
const SponsoredPackages: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { activeProfile } = useProfile();
  const navigate = useNavigate();

  const [packages, setPackages] = useState<SponsoredPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Success alert
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Redeem flow
  const [redeemingId, setRedeemingId] = useState<number | null>(null);
  const [confirmPkg, setConfirmPkg] = useState<SponsoredPackage | null>(null);

  // Copy feedback
  const [copiedId, setCopiedId] = useState<number | null>(null);

  /* ---------- Fetch packages ---------- */
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.sponsoredPackages.list);
      setPackages(response.data?.results ?? response.data ?? []);
    } catch (err: any) {
      console.error('Error fetching sponsored packages:', err);
      setError(t('services:sponsoredPackages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages, activeProfile]);

  /* ---------- Redeem ---------- */
  const handleRedeemClick = (pkg: SponsoredPackage) => {
    setConfirmPkg(pkg);
  };

  const confirmRedeem = async () => {
    if (!confirmPkg) return;
    setRedeemingId(confirmPkg.id);
    setConfirmPkg(null);
    try {
      await axiosInstance.post(API_ENDPOINTS.sponsoredPackages.redeem(confirmPkg.id));
      setSuccessMessage(t('services:sponsoredPackages.redeemSuccess'));
      await fetchPackages();
      // Auto-dismiss success after 6 seconds
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (err: any) {
      const msg = err?.response?.data?.detail
        || err?.response?.data?.error
        || t('services:sponsoredPackages.failedToRedeem');
      setError(msg);
    } finally {
      setRedeemingId(null);
    }
  };

  /* ---------- Copy coupon code ---------- */
  const handleCopy = async (code: string, pkgId: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(pkgId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(pkgId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  /* ---------- Render: badge for status ---------- */
  const renderBadge = (pkg: SponsoredPackage) => {
    if (pkg.my_redemption) {
      return <span className="sp-badge sp-badge--redeemed">{t('services:sponsoredPackages.redeemed')}</span>;
    }
    if (pkg.is_expired) {
      return <span className="sp-badge sp-badge--expired">{t('services:sponsoredPackages.expired')}</span>;
    }
    if (pkg.is_upcoming) {
      return <span className="sp-badge sp-badge--upcoming">{t('services:sponsoredPackages.unlockingSoon')}</span>;
    }
    if (pkg.is_available) {
      return <span className="sp-badge sp-badge--available">{t('services:sponsoredPackages.available')}</span>;
    }
    return null;
  };

  /* ---------- Render: action area ---------- */
  const renderActions = (pkg: SponsoredPackage) => {
    const isRedeeming = redeemingId === pkg.id;

    // Redeemed (show coupon code -- viewable even after expiry)
    if (pkg.my_redemption) {
      return (
        <div className="sp-coupon-area">
          <span className="sp-coupon-label">{t('services:sponsoredPackages.yourCouponCode')}</span>
          <div className={`sp-coupon-box${copiedId === pkg.id ? ' sp-coupon-box--copied' : ''}`}>
            <span className="sp-coupon-code">{pkg.my_redemption.coupon_code}</span>
            <button
              className={`sp-coupon-copy-btn${copiedId === pkg.id ? ' sp-coupon-copy-btn--copied' : ''}`}
              onClick={() => handleCopy(pkg.my_redemption!.coupon_code, pkg.id)}
              title="Copy coupon code"
            >
              <FontAwesomeIcon icon={copiedId === pkg.id ? faCheck : faCopy} />
            </button>
          </div>
          <span className="sp-redeemed-date">
            <FontAwesomeIcon icon={faCalendarDays} />
            {t('services:sponsoredPackages.redeemedOn', { date: formatDate(pkg.my_redemption.redeemed_at) })}
          </span>
        </div>
      );
    }

    // Expired (not redeemed)
    if (pkg.is_expired) {
      return (
        <button className="sp-expired-btn" disabled>
          <FontAwesomeIcon icon={faLock} />
          {t('services:sponsoredPackages.packageExpired')}
        </button>
      );
    }

    // Upcoming (future start date)
    if (pkg.is_upcoming) {
      return (
        <button className="sp-upcoming-btn" disabled>
          <FontAwesomeIcon icon={faHourglass} />
          {t('services:sponsoredPackages.availableFrom', { date: formatDate(pkg.valid_from) })}
        </button>
      );
    }

    // Available for redemption
    if (pkg.is_available) {
      return (
        <button
          className="sp-redeem-btn"
          onClick={() => handleRedeemClick(pkg)}
          disabled={isRedeeming}
        >
          {isRedeeming ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="sp-btn-spinner" />
              {t('services:sponsoredPackages.redeeming')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faGift} />
              {t('services:sponsoredPackages.redeemPackage')}
            </>
          )}
        </button>
      );
    }

    return null;
  };

  /* ---------- Render: skeleton loading ---------- */
  const renderSkeletons = () => (
    <div className="sp-packages-grid">
      {[1, 2, 3].map((i) => (
        <div className="sp-skeleton-card" key={i}>
          <div className="sp-skeleton-header">
            <div className="sp-skeleton-line sp-skeleton-title" />
            <div className="sp-skeleton-line sp-skeleton-badge" />
          </div>
          <div className="sp-skeleton-body">
            <div className="sp-skeleton-line sp-skeleton-desc" />
            <div className="sp-skeleton-line sp-skeleton-desc-short" />
            <div className="sp-skeleton-tags">
              <div className="sp-skeleton-line sp-skeleton-tag" />
              <div className="sp-skeleton-line sp-skeleton-tag" />
              <div className="sp-skeleton-line sp-skeleton-tag" />
            </div>
            <div className="sp-skeleton-line sp-skeleton-validity" />
          </div>
          <div className="sp-skeleton-footer">
            <div className="sp-skeleton-line sp-skeleton-btn" />
          </div>
        </div>
      ))}
    </div>
  );

  /* ---------- Main render ---------- */
  return (
    <div className="sp-page">
      {/* ======== HEADER ======== */}
      <section className="sp-header">
        <Container>
          <div className="sp-header-inner">
            <BackButton />
            <h1 className="sp-header-title">
              <FontAwesomeIcon icon={faGift} className="sp-header-title-icon" />
              {t('services:sponsoredPackages.headerTitle')}
            </h1>
            <p className="sp-header-subtitle">
              {t('services:sponsoredPackages.headerSubtitle')}
            </p>
          </div>
        </Container>
      </section>

      {/* ======== CONTENT ======== */}
      <section className="sp-content">
        <Container>
          {/* Success Alert */}
          {successMessage && (
            <div className="sp-success-alert">
              <div className="sp-success-alert-icon">
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <div className="sp-success-alert-text">
                <strong>Success</strong>
                <span>{successMessage}</span>
              </div>
              <button
                className="sp-success-alert-close"
                onClick={() => setSuccessMessage(null)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && renderSkeletons()}

          {/* Error */}
          {!loading && error && (
            <div className="sp-error-state">
              <div className="sp-error-icon-wrapper">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </div>
              <h3>{t('services:sponsoredPackages.somethingWentWrong')}</h3>
              <p>{error}</p>
              <button className="sp-retry-btn" onClick={fetchPackages}>
                {t('services:sponsoredPackages.retry')}
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && packages.length === 0 && (
            <div className="sp-empty-state">
              <div className="sp-empty-icon-wrapper">
                <FontAwesomeIcon icon={faGift} />
              </div>
              <h3>{t('services:sponsoredPackages.noPackages')}</h3>
              <p>
                {t('services:sponsoredPackages.noPackagesDesc')}
              </p>
            </div>
          )}

          {/* Packages Grid */}
          {!loading && !error && packages.length > 0 && (
            <div className="sp-packages-grid">
              {packages.map((pkg) => (
                <div
                  className={`sp-card ${getCardStatusClass(pkg)}`}
                  key={pkg.id}
                >
                  {/* Card Header */}
                  <div className="sp-card-header">
                    <h3 className="sp-card-name">{pkg.name}</h3>
                    {renderBadge(pkg)}
                  </div>

                  {/* Card Body */}
                  <div className="sp-card-body">
                    {pkg.description && (
                      <p className="sp-card-description">{pkg.description}</p>
                    )}

                    {/* Tests Included */}
                    {pkg.tests_included && pkg.tests_included.length > 0 && (
                      <div className="sp-tests-section">
                        <span className="sp-tests-label">
                          <FontAwesomeIcon icon={faVial} />
                          {t('services:sponsoredPackages.testsIncluded')}
                        </span>
                        <div className="sp-tests-tags">
                          {pkg.tests_included.map((test, idx) => (
                            <span className="sp-test-tag" key={idx}>
                              {test}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Validity */}
                    <div className="sp-validity">
                      <div className="sp-validity-icon">
                        <FontAwesomeIcon icon={faCalendarDays} />
                      </div>
                      <span className="sp-validity-text">
                        {t('services:sponsoredPackages.validUntil', { date: formatDate(pkg.valid_until) })}
                      </span>
                    </div>

                    {/* Instructions */}
                    {pkg.instructions && (
                      <div className="sp-instructions">
                        <div className="sp-instructions-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </div>
                        <p className="sp-instructions-text">{pkg.instructions}</p>
                      </div>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div className="sp-card-actions">
                    {renderActions(pkg)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* ======== REDEEM CONFIRMATION MODAL ======== */}
      {confirmPkg && (
        <div className="sp-modal-overlay" onClick={() => setConfirmPkg(null)}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <div className="sp-modal-icon">
                <FontAwesomeIcon icon={faGift} />
              </div>
              <h3>{t('services:sponsoredPackages.redeemConfirmTitle')}</h3>
              <p>
                {t('services:sponsoredPackages.redeemConfirmDesc')}
              </p>
            </div>
            <div className="sp-modal-body">
              <div className="sp-modal-pkg-name">{confirmPkg.name}</div>
            </div>
            <div className="sp-modal-footer">
              <button
                className="sp-modal-cancel-btn"
                onClick={() => setConfirmPkg(null)}
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                className="sp-modal-confirm-btn"
                onClick={confirmRedeem}
                disabled={redeemingId === confirmPkg.id}
              >
                {redeemingId === confirmPkg.id ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    {t('services:sponsoredPackages.redeeming')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faGift} />
                    {t('services:sponsoredPackages.confirmRedeem')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsoredPackages;
